import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import {
  House,
  PawPrint,
  ChartBar,
  Gear,
  SignOut,
  Translate,
  Calendar,
  Receipt,
  ChartLineUp,
  Archive,
} from "@phosphor-icons/react";

export default function TopNav() {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("dashboard"), icon: House, testid: "nav-dashboard" },
    { to: "/patients", label: t("patients"), icon: PawPrint, testid: "nav-patients" },
    { to: "/archive", label: t("archiveNav"), icon: Archive, testid: "nav-archive" },
    { to: "/appointments", label: t("appointments"), icon: Calendar, testid: "nav-appointments" },
    { to: "/invoices", label: t("invoices"), icon: Receipt, testid: "nav-invoices" },
    { to: "/reports", label: t("reports"), icon: ChartBar, testid: "nav-reports" },
    { to: "/analytics", label: t("analytics"), icon: ChartLineUp, testid: "nav-analytics" },
    { to: "/settings", label: t("settings"), icon: Gear, testid: "nav-settings" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-pf-border">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-pf-primary text-white grid place-items-center shadow-card">
              <PawPrint size={20} weight="fill" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight text-pf-text">
              Pawfile
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-pf-primary-light text-pf-primary"
                      : "text-pf-text-secondary hover:text-pf-text hover:bg-pf-bg"
                  }`
                }
              >
                <l.icon size={16} weight="regular" />
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-lg border border-pf-border px-2.5 py-1.5 text-xs font-semibold text-pf-text-secondary hover:bg-pf-bg transition-colors"
            title="Language"
          >
            <Translate size={14} />
            <span className={lang === "en" ? "text-pf-primary" : ""}>EN</span>
            <span className="opacity-40">/</span>
            <span className={lang === "ro" ? "text-pf-primary" : ""}>RO</span>
          </button>

          <div className="flex items-center gap-2 pl-2 sm:pl-3 sm:border-l border-pf-border">
            {/* Mobile: compact name + role next to logout */}
            <div className="sm:hidden text-right leading-tight" data-testid="user-badge-mobile">
              <div className="text-xs font-semibold text-pf-text max-w-[10ch] truncate">{user?.name}</div>
              <div className="text-[10px] text-pf-text-secondary">
                {user?.role === "admin" && t("admin")}
                {user?.role === "veterinarian" && t("veterinarianRole")}
                {user?.role === "assistant" && t("assistant")}
              </div>
            </div>
            <div className="hidden sm:block text-right">
              <div className="text-xs text-pf-text-secondary leading-none">
                {user?.role === "admin" && t("admin")}
                {user?.role === "veterinarian" && t("veterinarianRole")}
                {user?.role === "assistant" && t("assistant")}
                {user?.role && !["admin","veterinarian","assistant"].includes(user.role) && user.role}
              </div>
              <div className="text-sm font-semibold text-pf-text leading-tight">{user?.name}</div>
              {user?.clinic_name && (
                <div className="text-[10px] text-pf-primary font-medium leading-tight mt-0.5" data-testid="current-clinic-badge">
                  {user.clinic_name}
                </div>
              )}
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="p-2 rounded-lg text-pf-text-secondary hover:bg-pf-bg hover:text-pf-text transition-colors"
              title={t("logOut")}
              aria-label={t("logOut")}
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="md:hidden border-t border-pf-border">
        <nav className="flex items-center overflow-x-auto px-3 py-2 gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium ${
                  isActive
                    ? "bg-pf-primary-light text-pf-primary"
                    : "text-pf-text-secondary hover:bg-pf-bg"
                }`
              }
            >
              <l.icon size={14} />
              {l.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
